<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Swiper from 'swiper'
import { Autoplay, Pagination } from 'swiper/modules'
import type { HomePageConfig } from '../../configs/types'
import BannerInlineCard from './BannerInlineCard.vue'
import { getBannerImage } from './bannerImage'
import { theme } from '@/config/brands'
import { superstoryTrack } from '@/track'
import { bookParams } from '@/composables/tracks/bookParameters'
import { useBrandListExposure } from '@/composables/tracks/useBrandExposure'
import { useElementClick } from '@/composables/tracks'
import 'swiper/css'
import 'swiper/css/pagination'

const props = defineProps<{
  module: any
  config: HomePageConfig
  position: 'top' | 'inline'
  gotoBook: (id: string, firstChapterId?: string) => void
}>()

const emit = defineEmits<{
  (e: 'update:bg-url', url: string): void
}>()

const { elementClick } = useElementClick()
const primaryColor = theme.primaryColor

// 顶部 Banner 圆角 / 底部渐变蒙层均由各品牌 home config 决定
const topBannerRadius = computed(() => props.config.theme.topBannerRadius ?? '12px')
const topBannerBottomGradient = computed(() => props.config.theme.topBannerBottomGradient || '')
const topBannerIndicatorActiveColor = computed(() => props.config.theme.topBannerIndicatorActiveColor || '#ffffff')
const hasBottomGradient = computed(() => !!topBannerBottomGradient.value)
// 底部蒙层高度（缺省 80px = 改造前写死值）
const bottomMaskHeight = computed(() => props.config.theme.topBannerBottomMaskHeight || '80px')
// 顶部 banner 是否叠加书名 + 副文案（仅 top 模式、且品牌显式开启）
const showBannerText = computed(() => props.position === 'top' && props.config.theme.topBannerShowText === true)

// banner 背景图: 按 showType 选择封面图或自定义背景图
function getBannerBg(record: any) {
  return getBannerImage(record)
}

const banners = computed(() => {
  const records: any[] = props.module?.resourceList?.records || []
  return records.filter(r => !!getBannerBg(r))
})

const exposureRef = useBrandListExposure(() => props.position === 'top' ? banners.value : [], (record, index) => bookParams({ ...record, module_id: props.module.id, module_name: props.module.showName }, index + 1), 'book_exp')
const swiper = ref<Swiper | null>(null)
const swiperRef = ref<HTMLElement | null>(null)
const paginationRef = ref<HTMLElement | null>(null)
const enableSwiper = computed(() => banners.value.length > 1)

// 当前展示的 banner（供文案层跟随轮播切换）
const activeIndex = ref(0)
const activeRecord = computed<any>(() => banners.value[activeIndex.value] || banners.value[0] || null)

function handleClick(record: any) {
  if (!record?.id)
    return
  elementClick(`banner_${props.module.id}_${record.id}`)
  superstoryTrack('column_ck', bookParams({ ...record, module_id: props.module.id, module_name: props.module.showName }, banners.value.indexOf(record) + 1))
  props.gotoBook(record.id, record.firstChapter?.id)
}

function syncBgUrl(index: number) {
  activeIndex.value = index
  const url = getBannerBg(banners.value[index])
  if (!url)
    return
  if (props.position === 'top')
    emit('update:bg-url', url)
}

function initSwiper() {
  if (!swiperRef.value || !enableSwiper.value)
    return
  swiper.value = new Swiper(swiperRef.value, {
    modules: [Pagination, Autoplay],
    grabCursor: true,
    centeredSlides: false,
    slidesPerView: 1,
    spaceBetween: 12,
    observer: true,
    observeParents: true,
    loop: true,
    autoplay: {
      delay: 4000,
      disableOnInteraction: false,
    },
    pagination: paginationRef.value
      ? {
          el: paginationRef.value,
          clickable: true,
        }
      : false,
    on: {
      init: (swiperInstance) => {
        syncBgUrl(swiperInstance.realIndex)
      },
      slideChange: (swiperInstance) => {
        syncBgUrl(swiperInstance.realIndex)
      },
      click(swiperInstance: any, event) {
        const clickedSlide = (event.target as Element)?.closest('.swiper-slide')
        if (!clickedSlide)
          return
        if (clickedSlide.classList.contains('swiper-slide-active'))
          handleClick(banners.value[swiperInstance.realIndex])
        else
          swiperInstance.slideToClickedSlide()
      },
    },
  })
}

watch(banners, () => {
  if (swiper.value) {
    swiper.value.destroy(true, true)
    swiper.value = null
  }
  nextTick(() => {
    initSwiper()
    syncBgUrl(0)
  })
})

onMounted(() => {
  if (enableSwiper.value)
    initSwiper()
  syncBgUrl(0)
})

onBeforeUnmount(() => {
  if (swiper.value) {
    swiper.value.destroy(true, true)
    swiper.value = null
  }
})
</script>

<template>
  <div v-if="banners.length" ref="exposureRef" class="module-banner" :class="[position, { 'has-banner-text': showBannerText }]">
    <!-- 顶部模式：纯 backgroundImageUrl 大图轮播 -->
    <template v-if="position === 'top'">
      <!-- 单条：不走 Swiper -->
      <div
        v-if="!enableSwiper"
        class="banner-top-single" data-exposure-index="0"
        @click="handleClick(banners[0])"
      >
        <div class="banner-top-img" :style="{ backgroundImage: `url(${getBannerBg(banners[0])})` }" />
        <div class="banner-top-mask" />
        <div v-if="hasBottomGradient" class="banner-bottom-mask" />
        <div v-if="showBannerText && activeRecord" class="banner-top-text">
          <p class="banner-top-title">
            {{ activeRecord.contentName }}
          </p>
          <p v-if="activeRecord.introduce" class="banner-top-sub">
            {{ activeRecord.introduce }}
          </p>
        </div>
      </div>

      <!-- 多条：Swiper -->
      <div v-else class="banner-top-wrap">
        <div ref="swiperRef" class="banner-top swiper">
          <div class="swiper-wrapper">
            <div
              v-for="(record, index) in banners"
              :key="record.id || index"
              class="swiper-slide" :data-exposure-index="index"
            >
              <div class="banner-top-img" :style="{ backgroundImage: `url(${getBannerBg(record)})` }" />
            </div>
          </div>
          <div ref="paginationRef" class="swiper-pagination" />
        </div>
        <div class="banner-top-mask" />
        <div v-if="hasBottomGradient" class="banner-bottom-mask" />
        <div v-if="showBannerText && activeRecord" class="banner-top-text">
          <p class="banner-top-title">
            {{ activeRecord.contentName }}
          </p>
          <p v-if="activeRecord.introduce" class="banner-top-sub">
            {{ activeRecord.introduce }}
          </p>
        </div>
      </div>
    </template>

    <!-- 非顶部模式：左书封 + 右描述卡片，背景为各自 backgroundImageUrl 模糊化 -->
    <template v-else>
      <!-- 单条：不走 Swiper -->
      <div v-if="!enableSwiper" class="banner-inline-click" @click="handleClick(banners[0])">
        <BannerInlineCard :record="banners[0]" :module="module" />
      </div>

      <!-- 多条：Swiper -->
      <div v-else class="banner-inline-wrap">
        <div ref="swiperRef" class="banner-inline swiper">
          <div class="swiper-wrapper">
            <div
              v-for="(record, index) in banners"
              :key="record.id || index"
              class="swiper-slide banner-inline-click"
            >
              <BannerInlineCard :record="record" :module="module" />
            </div>
          </div>
          <div ref="paginationRef" class="swiper-pagination" />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.module-banner {
  width: 100%;
}

/* ---------- 顶部模式：大背景轮播 ---------- */
.banner-top-single,
.banner-top-wrap {
  width: 100%;
  aspect-ratio: 750 / 830;
  position: relative;
  overflow: hidden;
  border-bottom-left-radius: v-bind(topBannerRadius);
  border-bottom-right-radius: v-bind(topBannerRadius);
}

.banner-top {
  width: 100%;
  height: 100%;
}

/* 强制 swiper 内部撑满高度（Swiper 注入的元素需要 :deep 穿透 scoped） */
.banner-top :deep(.swiper-wrapper),
.banner-top :deep(.swiper-slide) {
  width: 100%;
  height: 100%;
}

/* 用 background-image + cover 代替 img，规避 img 高度继承在 scoped 下不稳定的问题 */
.banner-top-img {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
}

/* 顶部渐变蒙层：让 sticky-header 的搜索/Tab 在大图上更可读 */
.banner-top-mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 116px;
  pointer-events: none;
  z-index: 3;
  background-image: linear-gradient(179deg, rgba(19,22,29,0.90) 0%, rgba(19,22,29,0.40) 64%, rgba(19,22,29,0.00) 100%);
}

/* 底部渐变蒙层：使各品牌可在 home config 中配置 topBannerBottomGradient 启用 */
.banner-bottom-mask {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: v-bind(bottomMaskHeight);
  pointer-events: none;
  z-index: 5;
  background: v-bind(topBannerBottomGradient);
}

/* 顶部 banner 文案层（仅 topBannerShowText = true 时渲染） */
.banner-top-text {
  position: absolute;
  left: 15px;
  right: 15px;
  bottom: 30px;
  z-index: 6;
  pointer-events: none;
}

.banner-top-title {
  font-family: 'Baloo 2', 'Nunito Sans', system-ui, -apple-system, sans-serif;
  font-size: 22px;
  line-height: 28px;
  font-weight: 700;
  color: #ffffff;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.banner-top-sub {
  margin-top: 4px;
  font-size: 12px;
  line-height: 16px;
  color: rgba(255, 255, 255, 0.85);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 有文案时分页器下移贴底，避免与书名重叠 */
.module-banner.has-banner-text .banner-top :deep(.swiper-pagination) {
  bottom: 10px;
}

.banner-top :deep(.swiper-pagination) {
  position: absolute;
  bottom: 14px;
  left: 0;
  right: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* top 模式分页器：未选中保持白色，选中跟随品牌主色 */
.banner-top :deep(.swiper-pagination-bullet) {
  background: #ffffff;
  opacity: 0.5;
}

.banner-top :deep(.swiper-pagination-bullet-active) {
  background: v-bind(topBannerIndicatorActiveColor);
  opacity: 1;
}

/* ---------- 非顶部模式 ---------- */
.banner-inline-wrap {
  position: relative;
  width: 100%;
}

.banner-inline {
  position: relative;
  width: 100%;
  height: 121px;
}

/* swiper 容器/包裹/slide 都贴合卡片高度，消除 swiper 自带的多余高度 */
.banner-inline :deep(.swiper-wrapper) {
  height: 121px;
}

.banner-inline :deep(.swiper-slide) {
  width: 100%;
  height: 121px;
  box-sizing: border-box;
}

.banner-inline-click {
  cursor: pointer;
}

/* ---------- 顶部模式分页器 bullet（白色，叠在大图上） ---------- */
:deep(.swiper-pagination-bullet) {
  width: 6px;
  height: 6px;
  background: #ffffff;
  border-radius: 3px;
  opacity: 0.5;
  margin: 0 4px;
}

:deep(.swiper-pagination-bullet-active) {
  width: 20px;
  height: 6px;
  background: v-bind(primaryColor);
  border-radius: 3px;
  opacity: 1;
}

/* 非顶部分页器：卡片内底部居中，尺寸更小 */
.banner-inline :deep(.swiper-pagination) {
  position: absolute;
  bottom: 8px;
  left: 0;
  right: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.banner-inline :deep(.swiper-pagination-bullet) {
  width: 5px;
  height: 5px;
  background: #ffffff;
  border-radius: 3px;
  opacity: 0.5;
  margin: 0 3px;
}

.banner-inline :deep(.swiper-pagination-bullet-active) {
  width: 14px;
  height: 5px;
  background: v-bind(primaryColor);
  border-radius: 3px;
  opacity: 1;
}
</style>
